import React, { useState, useRef, useEffect } from 'react';

// Autocomplétion d'adresses/lieux.
// mode="city"    → communes via la Base Adresse Nationale (api-adresse.data.gouv.fr), affiche « Ville (CP) ».
// mode="address" → lieux nommés (gymnase, plage, salle…) via Nominatim (OSM) ; orienté par `city` (texte libre conservé).
export default function AddressAutocomplete({
  mode = 'city', value, onChange, onSelect, city, placeholder, inputStyle, disabled,
}) {
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const timer = useRef(null);
  const boxRef = useRef(null);

  useEffect(() => {
    const onOutside = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onOutside);
    return () => document.removeEventListener('mousedown', onOutside);
  }, []);

  const handleInput = (e) => {
    const q = e.target.value;
    onChange(q);
    if (timer.current) clearTimeout(timer.current);
    if (q.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    setLoading(true);
    timer.current = setTimeout(async () => {
      try {
        if (mode === 'address') {
          const params = new URLSearchParams({ format: 'json', limit: '7', countrycodes: 'fr', q: city ? `${q} ${city}` : q });
          const res = await fetch(`https://nominatim.openstreetmap.org/search?${params.toString()}`);
          const data = await res.json();
          setSuggestions(data || []);
          setOpen((data || []).length > 0);
        } else {
          const params = new URLSearchParams({ q, limit: '7', type: 'municipality' });
          const res = await fetch(`https://api-adresse.data.gouv.fr/search/?${params.toString()}`);
          const data = await res.json();
          setSuggestions(data.features || []);
          setOpen((data.features || []).length > 0);
        }
      } catch { setSuggestions([]); } finally { setLoading(false); }
    }, 350);
  };

  const pick = (f) => {
    if (mode === 'address') {
      const label = (f.display_name || '').split(',')[0] || f.display_name;
      onChange(label);
      setOpen(false);
      if (onSelect) onSelect({ label, name: label, lat: parseFloat(f.lat), lng: parseFloat(f.lon) });
      return;
    }
    const p = f.properties;
    const label = `${p.city || p.name} (${p.postcode})`;
    onChange(label);
    setOpen(false);
    if (onSelect) onSelect({
      label, name: p.name, city: p.city || p.name, postcode: p.postcode, citycode: p.citycode,
      context: p.context, lat: f.geometry?.coordinates?.[1] ?? null, lng: f.geometry?.coordinates?.[0] ?? null,
    });
  };

  return (
    <div ref={boxRef} style={{ position: 'relative' }}>
      <input
        value={value} onChange={handleInput} placeholder={placeholder} autoComplete="off" disabled={disabled}
        onFocus={() => suggestions.length && setOpen(true)}
        style={inputStyle}
      />
      {loading && <span style={{ position: 'absolute', right: 12, top: 12, fontSize: 11, color: 'var(--text-3)' }}>…</span>}
      {open && suggestions.length > 0 && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 40, marginTop: 4,
          background: 'var(--ink-2, #fff)', border: '1px solid var(--line)', borderRadius: 12,
          boxShadow: '0 12px 32px rgba(20,20,40,0.14)', maxHeight: 240, overflowY: 'auto',
        }}>
          {suggestions.map((f, i) => {
            const [main, ...rest] = mode === 'address'
              ? (f.display_name || '').split(',').map(s => s.trim())
              : [f.properties.city || f.properties.name, `${f.properties.postcode} · ${f.properties.context}`];
            const sub = mode === 'address' ? rest.join(', ') : rest[0];
            return (
              <div key={i} onMouseDown={(e) => { e.preventDefault(); pick(f); }}
                style={{ padding: '10px 13px', cursor: 'pointer', borderBottom: i < suggestions.length - 1 ? '1px solid var(--line)' : 'none' }}>
                <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text)' }}>{main}</div>
                <div style={{ fontSize: 12, color: 'var(--text-3)' }}>{sub}</div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
